/* eslint-env browser */
import { Environments } from '../config/environments'
import appConfig from '../config'
import removeAccents from 'remove-accents'
import { HttpMethods, URIEncode } from './apiHelper'
import { EUROPEAN_DATE, SUPPORTED_LANGUAGE_LOCALES, Dimensions, Contexts, SupportedRoles, OtherRoles, SchoolRoles, InstitutionRoles, Keyboard } from './constants'
import moment from 'moment'
import { isArray, isString, isFinite, isFunction, isObject as _isObject, differenceBy as _differenceBy } from 'lodash'
import { Properties } from './entities'

/** @enum {string} */
export const FormatErrors = Object.freeze({
  CANNOT_BE_NULL: 'CANNOT_BE_NULL',
  LANGUAGE_NOT_SUPPORTED: 'LANGUAGE_NOT_SUPPORTED',
  ROLE_NOT_SUPPORTED: 'ROLE_NOT_SUPPORTED',
  NOT_A_VALID_STRING: 'NOT_A_VALID_STRING',
  CANNOT_BE_EMPTY: 'CANNOT_BE_EMPTY',
  INVALID_EMAIL_FORMAT: 'INVALID_EMAIL_FORMAT'
})

function getEnvironment () {
  const envs = [
    { url: 'localhost', env: Environments.DEV },
    { url: 'feature-quota-rework-part1.review.interneo.pro', env: Environments.QUOTAS },
    { url: 'sandbox.interneo.pro', env: Environments.SANDBOX },
    { url: 'review.interneo.pro', env: Environments.SANDBOX },
    { url: 'demo.interneo.pro', env: Environments.TEST },
    { url: 'staging.interneo.pro', env: Environments.STAGING },
    { url: 'app.interneo.pro', env: Environments.PROD },
    { url: 'app.sandbox.interneo.pro.ovh04.opalsolutions.be', env: Environments.NEW_SANDBOX },
    { url: 'app.staging.interneo.pro.ovhpc01.opalsolutions.be', env: Environments.NEW_STAGING }
  ]

  const filteredEnvs = envs.filter(e => window.location.href.indexOf(e.url) > -1)

  if (filteredEnvs.length > 0) {
    return filteredEnvs[0].env
  }

  return Environments.TEST
}

function getApiUrl () {
  if (getEnvironment() === Environments.DEV) return appConfig.devServer
  if (getEnvironment() === Environments.QUOTAS) return appConfig.quotasServer
  if (getEnvironment() === Environments.SANDBOX) return appConfig.sandboxServer
  if (getEnvironment() === Environments.TEST) return appConfig.testServer
  if (getEnvironment() === Environments.PROD) return appConfig.prodServer
  if (getEnvironment() === Environments.STAGING) return appConfig.stagingServeur
  if (getEnvironment() === Environments.NEW_SANDBOX) return appConfig.newSandboxServer
  if (getEnvironment() === Environments.NEW_STAGING) return appConfig.newStagingServer
}

function getGoogleTrackingId () {
  if (getEnvironment() === Environments.DEV) return appConfig.googleTrackingIdDev
  if (getEnvironment() === Environments.QUOTAS) return appConfig.googleTrackingIdDev
  if (
    getEnvironment() === Environments.STAGING ||
    getEnvironment() === Environments.TEST ||
    getEnvironment() === Environments.NEW_STAGING
  ) {
    return appConfig.googleTrackingIdDev
  }
  if (getEnvironment() === Environments.PROD) return appConfig.googleTrackingIdProd
}

/**
 * Wraps fetch API into a direct JSON request so as to
 * spare some typing.
 *
 * @param  String path URL to send the request to.
 *
 * @return Promise
 */
function fetchJSON (path) {
  return window.fetch(`${getApiUrl()}${path}`).then(response => {
    if (response.status >= 200) {
      return Promise.reject(
        new Error(
          `Error with request to ${response.url} : [${response.status}] ${response.statusText}`
        )
      )
    }

    return response.json()
  })
}

/**
 * @deprecated
 */
function request (path, method, body, user, params) {
  if (typeof params === 'undefined') {
    params = {
      catchError: true,
      catchEntityDeletedError: true
    }
  } else {
    params = {
      catchError: typeof params.catchError === 'undefined' ? true : params.catchError,
      catchEntityDeletedError: typeof params.catchEntityDeletedError === 'undefined' ? true : params.catchEntityDeletedError
    }
  }

  path = getApiUrl() + path
  if (
    typeof user !== 'undefined' &&
    user !== null &&
    typeof user.switchUser !== 'undefined' &&
    user.switchUser !== null
  ) {
    const params = path.split('?')[1]

    if (params) {
      path = path.split('?')[0]
    }
    const switchUserParams = '?_switch_user=' + URIEncode(user.switchUser)
    path += params ? switchUserParams + '&&' + params : switchUserParams
  }
  const token =
    typeof user !== 'undefined' &&
    user !== null &&
    typeof user.token !== 'undefined' &&
    user.token !== null
      ? user.token
      : null

  const req = new window.Request(path, {
    method: method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    referrerPolicy: 'no-referrer-when-downgrade',
    body: body ? JSON.stringify(body) : undefined
  })

  return window
    .fetch(req)
    .then(response => {
      if (response.status === 418) return 'password_change'
      if (response.status === 419) return 'password_change_set_optins'
      if (response.status === 420) return 'password_change_set_optins_user' // this means the current user is not also a student so we don't show the agreement for "recruitment purposes"
      if (response.status === 421) return 'set_optins'
      if (response.status === 422) return 'set_optins_user' // this means the current user is not also a student so we don't show the agreement for "recruitment purposes"
      return response.json()
    })
    .then(json => {
      if (json.code === 403 && !params.catchError) {
        return json
      }
      if (json.code >= 400 && params.catchError && params.catchEntityDeletedError) {
        document.dispatchEvent(
          new window.CustomEvent('api-error', { detail: { code: json.code, errorDetails: json.message } })
        )
      }

      return json
    })
    .catch(() => {
      document.dispatchEvent(
        new window.CustomEvent('api-error', { detail: { code: 408 } })
      )
    })
}

export const fetch = async (path, method, body, user, headers) => {
  return requestWithPromise(path, method, body, user, true, true, null, headers)
}

async function requestWithPromise (path, method, body, user, disableErrorHandler = false, followPromiseError = false, apiUrl = null, requestHeaders = []) {
  const token = user && user.token ? user.token : null

  path = apiUrl ? apiUrl + path : getApiUrl() + path

  if (user) {
    if (user.switchUser) {
      const splitPath = path.split('?')
      const switchUserParams = '?_switch_user=' + URIEncode(user.switchUser)

      splitPath[0] += splitPath[1] ? switchUserParams + '&' + splitPath[1] : switchUserParams

      path = splitPath[0]
    }
  }

  const commonHeaders = new Headers({ 'Content-Type': 'application/json' })

  if (token !== null) {
    commonHeaders.append('Authorization', `Bearer ${token}`)
  }

  const headers = requestHeaders.reduce((acc, curr) => {
    if (curr instanceof Headers) {
      curr.forEach((value, key) => {
        acc.append(key, value)
      })
    }

    return acc
  }, commonHeaders)

  const req = new window.Request(path, {
    method,
    headers,
    referrerPolicy: 'no-referrer-when-downgrade',
    body: body ? JSON.stringify(body) : undefined
  })

  return window.fetch(req).then(response => {
    if (!response.ok) {
      throw response
    }

    return response.json()
  }).catch(response => {
    if (!disableErrorHandler) {
      document.dispatchEvent(
        new window.CustomEvent('api-error-displayer', { detail: response })
      )
    }

    if (followPromiseError) {
      throw response
    }
  })
}

function requestImage (path, method, body, user, params, oldUrl = null) {
  if (typeof params === 'undefined') {
    params = {
      catchError: true,
      catchEntityDeletedError: true
    }
  } else {
    params = {
      catchError: typeof params.catchError === 'undefined' ? true : params.catchError,
      catchEntityDeletedError: typeof params.catchEntityDeletedError === 'undefined' ? true : params.catchEntityDeletedError
    }
  }

  path = getApiUrl() + path
  if (
    typeof user !== 'undefined' &&
    user !== null &&
    typeof user.switchUser !== 'undefined' &&
    user.switchUser !== null
  ) {
    const params = path.split('?')[1]

    if (params) {
      path = path.split('?')[0]
    }
    const switchUserParams = '?_switch_user=' + URIEncode(user.switchUser)
    path += params ? switchUserParams + '&&' + params : switchUserParams
  }
  const token =
    typeof user !== 'undefined' &&
    user !== null &&
    typeof user.token !== 'undefined' &&
    user.token !== null
      ? user.token
      : null

  const req = new window.Request(path, {
    method: method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    referrerPolicy: 'no-referrer-when-downgrade',
    body: body ? JSON.stringify(body) : undefined
  })

  return window
    .fetch(req)
    .then(response => response.blob())
    .then(blob => {
      return URL.createObjectURL(blob)
    })
    .catch(() => {
      document.dispatchEvent(
        new window.CustomEvent('api-error', { detail: { code: 408 } })
      )
    })
}

async function getJobResultWithPromise (jobId, user) {
  let path = getApiUrl() + '/job-result/' + jobId
  const token = isValidUserToken(user) ? user.token : null

  if (jobId === null) {
    throw new Error('jobId cannot be null')
  }

  if (!isValidUser(user)) {
    throw new Error('User must be an object')
  }

  if (inImpersonateMode(user)) {
    path = addSwitchUserParameterToUrl(path, user)
  }

  const req = getNewWindowRequest(path, HttpMethods.GET, token, null)

  return window.fetch(req)
    .then(response => {
      if (!response.ok) {
        return Promise.reject(response)
      }

      return response.json()
    })
}

function downloadFileWithPromise (path, fileName, user) {
  const method = HttpMethods.GET

  if (!fileName) {
    throw new Error('Filename is mandatory')
  }

  if (!isValidUser(user)) {
    throw new Error('User must be an object')
  }

  if (inImpersonateMode(user)) {
    path = addSwitchUserParameterToUrl(path, user)
  }

  const token = isValidUserToken(user)
    ? user.token
    : null

  return window
    .fetch(`${getApiUrl()}${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      referrerPolicy: 'no-referrer-when-downgrade',
      method: method,
      body: undefined
    })
    .then(async response => {
      if (response.status >= 400) {
        return Promise.reject(response)
      }

      return response.blob()
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
    })
}

const getNewWindowRequest = (path, method, token, body) => {
  const req = new window.Request(path, {
    method: method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    referrerPolicy: 'no-referrer-when-downgrade',
    body: body ? JSON.stringify(body) : undefined
  })

  return req
}

const isValidUser = user => {
  return !!user
}

const inImpersonateMode = user => {
  return !!user.switchUser
}

const isValidUserToken = user => {
  return !!user.token
}

const addSwitchUserParameterToUrl = (path, user) => {
  const params = path.split('?')[1]
  const url = path.split('?')[0]

  const switchUserParams = '?_switch_user=' + URIEncode(user.switchUser)
  const newPath = url + switchUserParams

  return params ? newPath + '&' + params : newPath
}

function downloadFile (path, fileName, user, method, body) {
  if (!fileName) {
    throw new Error('Filename is mandatory')
  }
  method = typeof method === 'undefined' ? HttpMethods.GET : method
  body = typeof method === 'undefined' ? null : JSON.stringify(body)

  if (
    typeof user !== 'undefined' &&
    user !== null &&
    typeof user.switchUser !== 'undefined' &&
    user.switchUser !== null
  ) {
    const params = path.split('?')[1]

    if (params) {
      path = path.split('?')[0]
    }
    const switchUserParams = '?_switch_user=' + URIEncode(user.switchUser)
    path += params ? switchUserParams + '&&' + params : switchUserParams
  }

  const token =
    typeof user !== 'undefined' &&
    user !== null &&
    typeof user.token !== 'undefined' &&
    user.token !== null
      ? user.token
      : null
  return window
    .fetch(`${getApiUrl()}${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      referrerPolicy: 'no-referrer-when-downgrade',
      method: method,
      body: body
    })
    .then(async response => {
      if (response.status >= 400) {
        const json = await response.json()
        document.dispatchEvent(
          new window.CustomEvent('api-error', { detail: { code: json.code, errorDetails: json.message } })
        )
        return Promise.reject(
          new Error(
            `Error with request to ${response.url} : [${response.status}] ${response.statusText}`
          )
        )
      }

      return response.blob()
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      a.remove()
    })
}

function requestMultipart (path, method, body, user, responseIsNotJson = false) {
  const payload = {}
  const headers = {}

  const requestBody = new window.FormData()
  for (const property in body) {
    requestBody.append(property, body[property])
  }

  payload.body = requestBody

  path = getApiUrl() + path
  if (
    typeof user !== 'undefined' &&
    user !== null &&
    typeof user.switchUser !== 'undefined' &&
    user.switchUser !== null
  ) {
    path += '?_switch_user=' + URIEncode(user.switchUser)
  }
  const token =
    typeof user !== 'undefined' &&
    user !== null &&
    typeof user.token !== 'undefined' &&
    user.token !== null
      ? user.token
      : null

  if (isObject(user) && isString(user.token)) {
    headers.Authorization = `Bearer ${token}`
  }

  payload.method = method
  payload.headers = headers

  return window.fetch(path, payload).then(async response => {
    if (response.status >= 400) {
      const errorDetails = await response.json()
      if (errorDetails && errorDetails.message) {
        return Promise.reject(errorDetails)
      }
      return Promise.reject(
        new Error(
          `Error with request to ${response.url} : [${response.status}] ${response.statusText}`
        )
      )
    }
    if (responseIsNotJson) {
      return response
    }
    return response.json()
  })
}

function isNumeric (n) {
  return !isNaN(parseFloat(n)) && isFinite(n)
}

const isNumber = value => {
  return typeof value === 'number' && !isNaN(value) && isFinite(value)
}

export const isNull = value => value === null

const isPositive = (value, isStrictlyPositive = false) => isFinite(value) && value >= (isStrictlyPositive ? 1 : 0)

/** @enum {string} */
export const SmartTableInputTypes = Object.freeze({
  DATE: 'date',
  EMAIL: 'email',
  FREE_TEXT: 'freeText',
  FREE_TEXT_REQUIRED: 'freeTextRequired',
  NAME: 'name',
  PASSWORD: 'password',
  PHONE: 'phone',
  POSITIVE_INTEGER: 'positiveInteger',
  INTEGER: 'integer',
  USERNAME: 'username',
  ZIPCODE: 'zipcode'
})

function validateFormInput (type, string, mandatory = false) {
  if (
    mandatory &&
    (typeof string === 'undefined' || string === null || string.length === 0)
  ) {
    return false
  }

  if (!mandatory && typeof string !== 'undefined' && string !== null && string.length === 0) {
    return true
  }

  const inputTypes = {
    [SmartTableInputTypes.DATE]: new RegExp(
      /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{2}$/
    ),
    [SmartTableInputTypes.EMAIL]: new RegExp(
      /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    ),
    [SmartTableInputTypes.FREE_TEXT]: new RegExp(/^.{0,255}$/),
    [SmartTableInputTypes.FREE_TEXT_REQUIRED]: new RegExp(/^.{1,255}$/),
    [SmartTableInputTypes.NAME]: new RegExp(
      /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]*$/u
    ),
    [SmartTableInputTypes.PASSWORD]: new RegExp(/^.{8,255}$/),
    [SmartTableInputTypes.PHONE]: new RegExp(/^[0-9 ]*$/),
    [SmartTableInputTypes.POSITIVE_INTEGER]: new RegExp(/^[0-9]{1,10}$/),
    [SmartTableInputTypes.INTEGER]: new RegExp(/^[+\-0-9]{1,10}$/),
    [SmartTableInputTypes.USERNAME]: new RegExp(/^[A-Za-z0-9._-]{3,15}$/),
    [SmartTableInputTypes.ZIPCODE]: new RegExp(/^[0-9a-zA-Z-]+$/)
  }

  return inputTypes[type].test(string)
}

function objectDeepCopy (object) {
  return JSON.parse(JSON.stringify(object))
}

function getUrlParameterByName (name) {
  const url = window.location.href
  name = name.replace(/[\]]/g, '\\$&')
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)')
  const results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

function getCurrentUrl () {
  // Fix for IE.
  return window.location.origin
    ? window.location.protocol +
        '//' +
        window.location.hostname +
        (window.location.port ? ':' + window.location.port : '')
    : window.location.origin
}

function downloadCSV (array) {
  let csv = convertArrayOfObjectsToCSV({ data: array })

  if (csv == null) {
    return
  }

  if (!csv.match(/^data:text\/csv/i)) {
    csv = 'data:text/csv;charset=utf-8,' + csv
  }

  const filename = 'interneo-export.csv'
  const data = encodeURI(csv)
  const link = document.createElement('a')

  link.setAttribute('href', data)
  link.setAttribute('download', filename)

  document.body.appendChild(link)

  link.click()
  link.remove()
}

function convertArrayOfObjectsToCSV (args) {
  const data = args.data || null

  if (data == null || !data.length) {
    return null
  }

  const columnDelimiter = args.columnDelimiter || ','
  const lineDelimiter = args.lineDelimiter || '\n'
  const keys = Object.keys(data[0])
  let result = ''

  result += keys.join(columnDelimiter)
  result += lineDelimiter

  data.forEach(item => {
    keys.forEach((key, index) => {
      if (index > 0) {
        result += columnDelimiter
      }

      result += typeof item[key] === 'undefined' ? '' : item[key]
    })

    result += lineDelimiter
  })

  return result
}

function doesWeeksOverlap (startOfWeek1, endOfWeek1, startOfWeek2, endOfWeek2) {
  return (
    (endOfWeek1.isSameOrAfter(startOfWeek2) &&
      endOfWeek1.isSameOrBefore(endOfWeek2)) ||
    (startOfWeek1.isSameOrBefore(startOfWeek2) &&
      endOfWeek1.isSameOrAfter(endOfWeek2)) ||
    (startOfWeek1.isSameOrAfter(startOfWeek2) &&
      startOfWeek1.isSameOrBefore(endOfWeek2)) ||
    (startOfWeek1.isSameOrAfter(startOfWeek2) &&
      endOfWeek1.isSameOrBefore(endOfWeek2))
  )
}

function removeParentsOfSet (set) {
  // if we have only one item, don't remove it in case this is the parent!
  if (set.length === 1) {
    return set
  }

  set = set.map(s => {
    s.isParent = false
    return s
  })

  for (let i = 0; i < set.length; i++) {
    if (set[i].parent === null) {
      set[i].isParent = true
    }

    for (let j = 0; j < set.length; j++) {
      if (set[j].id === set[i].parent) {
        set[j].isParent = true
      }
    }
  }

  const setWithNoParents = []
  const removedParents = []

  set.forEach(item => {
    if (!item.isParent) {
      delete item.isParent
      setWithNoParents.push(item)
    } else {
      delete item.isParent
      removedParents.push(item)
    }
  })

  return ({ setWithNoParents, removedParents })
}

function getContextFromRoles (roles) {
  let context = Contexts.NONE

  if (roles.includes(OtherRoles.STUDENT)) {
    context = Contexts.STUDENT
  } else if (recursiveIncludes(roles, [SchoolRoles.COORDINATOR, SchoolRoles.SUPERVISOR, SchoolRoles.ENCODER, SchoolRoles.DOMAIN_EVALUATOR])) {
    context = Contexts.SCHOOL
  } else if (recursiveIncludes(roles, [InstitutionRoles.HOSPITAL_ADMIN, InstitutionRoles.NURSE, InstitutionRoles.INSTITUTION_GROUP_ADMIN, SchoolRoles.VALIDATOR, InstitutionRoles.OBSERVER])) {
    context = Contexts.INSTITUTION
  } else if (roles.includes(OtherRoles.ADMIN)) {
    context = Contexts.ADMIN
  }

  return context
}

function canonizeString (string) {
  return removeAccents(string.toString()).toLowerCase()
}

function filterSectionsAndSetRootSectionOfSchool (school, rootName) {
  const sections = school.sections
  const root = sections.filter(s => s.parent === null)[0]
  if (sections.length > 1) {
    // change the root name only when we have more sections
    root.name = rootName
  }
  school.rootSection = root
  const { setWithNoParents, removedParents } = removeParentsOfSet(sections)
  school.sections = setWithNoParents || []
  school.parentSections = removedParents || []
  return school
}

function generalErrorHandler (err) {
  console.error(err)
}

export function recursiveIncludes (array, elements) {
  for (let i = 0; i < elements.length; i++) {
    if (array.includes(elements[i])) {
      return true
    }
  }

  return false
}

/**
 * This method receives minutes and formats them to H:MM
 * @param {number} time
 */
function formatTimeFromMinutes (time, hForm = false) {
  const hours = parseInt(time / 60)
  let minutes = parseInt(time % 60)

  if (minutes < 10) {
    minutes = `0${minutes}`
  }

  return hForm ? `${hours}h${minutes}` : `${hours}:${minutes}`
}

/**
 * This method is used on the super admin emails list, to transform encoded french characters into human readable ones
 * @param {*} text
 */
function decodeFrenchCharacters (text) {
  text = text.replace(/&eacute;/g, 'é')
  text = text.replace(/&egrave;/g, 'è')
  text = text.replace(/&agrave;/g, 'á')
  text = text.replace(/&aacute;/g, 'à')
  return text
}

/**
 * This parses the url search query and transforms it into an object
 */
function urlSearchParamsToObject () {
  const paramsObject = {}
  const urlParams = new URLSearchParams(window.location.search).entries()

  for (const param of urlParams) {
    const [key, value] = param
    paramsObject[key] = value
  }

  return paramsObject
}

/**
 * Format an integer in a string type into an hours-minutes form
 *
 * @param {integer} integerValue the number to be formated
 *
 * @returns the hours-minutes form of the input number
 */
const formatPositiveIntegerIntoHours = (integerValue, onlyHours = false) => {
  if (onlyHours) {
    return parseInt(integerValue, 10) + 'h00'
  } else {
    let hours = integerValue.length > 2 ? integerValue.slice(0, integerValue.length - 2) : '0'
    let minutes = integerValue.slice(integerValue.length - 2)

    if (minutes >= 60) {
      hours = parseInt(hours, 10) + 1
      minutes -= 60
    }

    return parseInt(hours, 10).toString() + 'h' + (minutes > 9 ? parseInt(minutes, 10).toString() : '0' + parseInt(minutes, 10).toString())
  }
}

/**
 * normalize an hours string
 *
 * @param {string} stringHours the hours stirng to normalize
 *
 * @returns the normalize hours string
 */
const normalizedHoursString = (stringHours) => {
  const HoursminutesArray = stringHours.split('h')

  if (HoursminutesArray[1] >= 60) {
    HoursminutesArray[0] = parseInt(HoursminutesArray[0], 10) + 1
    HoursminutesArray[1] -= 60
  }

  return parseInt(HoursminutesArray[0], 10).toString() +
    'h' +
    (HoursminutesArray[1] > 9
      ? parseInt(HoursminutesArray[1], 10).toString()
      : '0' + parseInt(HoursminutesArray[1], 10).toString())
}

/**
 * convert an hours string into a number of minutes
 *
 * @param {string} stringHours the string hours to convert
 *
 * @returns the number of minutes contains in the hours string
 */
const hoursToMinutes = (stringHours) => {
  const hoursMinutesArray = stringHours.includes('h') ? stringHours.split('h') : [stringHours, '0']

  return hoursMinutesArray[0] * 60 + parseInt(hoursMinutesArray[1], 10)
}

const reverseDate = (initialDate, splitSymbol, joinSymbol) => {
  return initialDate.split(splitSymbol).reverse().join(joinSymbol)
}

const objectToArray = (object) => {
  const objectsArray = []

  Object.keys(object).forEach(key => {
    objectsArray.push(object[key])
  })

  return objectsArray
}

const arrayToObject = (array, keys) => {
  const object = {}

  array.forEach(item => {
    object[getItemValueRecursion(item, keys)] = item
  })

  return object
}

const getItemValueRecursion = (item, keys) => {
  if (!Array.isArray(keys)) {
    return item[keys]
  }

  let value = item

  keys.forEach(key => {
    value = value[key]
  })

  return value
}

const checkIfJsonIsCorrect = json => {
  return (
    (typeof json.error === 'undefined' || json.error === false) &&
    typeof json.data !== 'undefined'
  )
}

const toFirstUpperCase = text => {
  return text[0].toUpperCase() + text.slice(1, text.length)
}

const isObject = value => {
  return _isObject(value) && !isArray(value)
}

const dateStringOrNull = (value, format = EUROPEAN_DATE, key = null) => {
  let date = null

  if (isObject(value)) {
    const dateValue = key ? value[key] : value
    date = moment(dateValue).format(format)
  }
  return date
}

export const caseInsensitiveInclude = (s1, s2) => {
  let include = false

  if (typeof s1 === 'string' && typeof s2 === 'string') {
    include = s1.toLowerCase().includes(s2.toLowerCase())
  }

  return include
}

export const getPercentage = (numerator, denominator) => {
  let percentage = 0

  if (denominator) {
    percentage = (numerator / denominator) * 100
  }

  return percentage
}

export const isSupportedLanguage = language => {
  const report = {
    valid: true,
    errors: []
  }

  if (!language) {
    report.valid = false
    report.errors.push(FormatErrors.CANNOT_BE_EMPTY)
  } else if (!SUPPORTED_LANGUAGE_LOCALES.includes(language)) {
    report.valid = false
    report.errors.push(FormatErrors.LANGUAGE_NOT_SUPPORTED)
  }

  return report
}

export const isSupportedRoles = role => {
  const report = {
    valid: true,
    errors: []
  }

  if (!role) {
    report.valid = false
    report.errors.push(FormatErrors.CANNOT_BE_EMPTY)
  } else if (!Object.values(SupportedRoles).includes(role)) {
    report.valid = false
    report.errors.push(FormatErrors.ROLE_NOT_SUPPORTED)
  }

  return report
}

export const validateString = (string, conditions = {}) => {
  const report = {
    valid: true,
    errors: []
  }

  if (!conditions.nullable && string === null) {
    report.valid = false
    report.errors.push(FormatErrors.CANNOT_BE_NULL)
  } else if (conditions.isEmail && !validateFormInput('email', string, true)) {
    report.valid = false
    report.errors.push(FormatErrors.INVALID_EMAIL_FORMAT)
  } else if (!(conditions.nullable && string === null) && !isString(string)) {
    report.valid = false
    report.errors.push(FormatErrors.NOT_A_VALID_STRING)
  } else if (conditions.notEmpty && string === '') {
    report.valid = false
    report.errors.push(FormatErrors.CANNOT_BE_EMPTY)
  }

  return report
}

export const getAccessorByWidth = width => {
  let accessor = 'xs'

  if (Dimensions.XS < width && width <= Dimensions.SM) {
    accessor = 'sm'
  } else if (Dimensions.SM < width && width <= Dimensions.MD) {
    accessor = 'md'
  } else if (width > Dimensions.MD) {
    accessor = 'lg'
  }

  return accessor
}

export const getEmptyLabelOrValue = (string, type, translator) => {
  if (string === null) {
    string = translator('without_' + type.toLowerCase())
  }

  return string
}

export const getItemModifications = (newItems, oldItems) => {
  return newItems.reduce((acc, item) => {
    if (item.id === -1 || !acc.deleted.find(i => i === item.id)) {
      acc.added.push(item)
    } else {
      acc.deleted = acc.deleted.filter(i => i !== item.id)
    }

    return acc
  }, {
    added: [],
    deleted: oldItems.map(i => i.id)
  })
}

export const getLanguageByLocale = locale => {
  return locale.split('_')[0]
}

export const isNonEmptyArray = array => isArray(array) && array.length > 0
export const isEmptyArray = array => isArray(array) && array.length === 0

/** @returns {any[]} */
export const formatAsArray = (array) => isNonEmptyArray(array) ? array : []

export const firstOrDefault = (arrayLike, defaultValue = null) => arrayLike?.[0] ?? defaultValue

/** @returns {any[]} */
export const difference = (first = [], second = []) => {
  let diff = []

  if (isArray(first) && isArray(second)) {
    diff = diff.concat(first.filter(item => !second.includes(item)))
    diff = diff.concat(second.filter(item => !first.includes(item)))
  }

  return diff
}

export const differenceBy = (first = [], second = [], iteratee) => {
  const _first = _differenceBy(first, second, iteratee)
  const _second = _differenceBy(second, first, iteratee)

  return _first.concat(_second)
}

export const areMutuallyExcluded = (propA, propB) => propA == null || (propA != null && propB == null)

/**
 * Allows to safely spread an object, even if it is `undefined` or `null`
 * @param {object|undefined|null} object
 */
export const safeSpread = (object = null) => {
  return isObject(object) ? { ...object } : {}
}

export const getSectionAbbreviation = (section, translator) => {
  let abbreviation = translator('Without section')

  if (section) {
    abbreviation = section.sectionToYear ? section.sectionToYear.sectionAbbreviation : section.abbreviation
  }

  return abbreviation
}

export const isUUID = str => {
  const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi

  return regexExp.test(str)
}

export const getIdOrNull = object => {
  let id = null

  if (isNumber(object)) {
    id = object
  } else if (isObject(object)) {
    id = object.id
  } else if (isUUID(object)) {
    id = object
  } else if (isString(object)) {
    id = parseInt(object, 10)
  }

  if (id !== null && !isUUID(id)) {
    id = parseInt(id, 10)
  }

  return id
}

export const getTrimmedOrNull = value => {
  return isString(value) ? value.trim() : null
}

export const getEntityOrNull = object => {
  let idOrNull = null
  let entity = null

  if (isObject(object) && object.id != null) {
    entity = object
  } else if ((idOrNull = getIdOrNull(object)) !== null) {
    entity = { id: idOrNull }
  }

  return entity
}

export const hasValidId = object => {
  const id = getIdOrNull(object)

  return isNumber(id) || isUUID(id)
}

export const sortById = (a, b) => {
  let sortValue = 0

  const idA = getIdOrNull(a)
  const idB = getIdOrNull(b)

  if (isNumber(idA) && isNumber(idB)) {
    sortValue = idA - idB
  } else if (isString(idA) && isString(idB)) {
    sortValue = idA.toLowerCase().localeCompare(idB.toLowerCase())
  }

  return sortValue
}

export const isOptimistic = object => getIdOrNull(object) < 0

export const replaceBorderColor = (border, color) => {
  const borderParts = border.split(' ')

  borderParts[2] = color

  return borderParts.join(' ')
}

export const replaceBorderSize = (border, size) => {
  const borderParts = border.split(' ')

  borderParts[0] = size

  return borderParts.join(' ')
}

export const getMatrix = (width, height, initialValue = 0) => {
  const matrix = []

  for (let i = 0; i < height; i++) {
    matrix.push(new Array(width).fill(initialValue))
  }

  return matrix
}

export const floatRound = (value, precision) => {
  return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision)
}

export const deepSpread = obj => {
  if (isObject(obj)) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (isObject(value)) {
        acc[key] = deepSpread(value)
      } else {
        acc[key] = value
      }

      return acc
    }, {})
  }

  return obj
}

export const deepFuse = (obj1, obj2) => {
  const newObject = deepSpread(obj1)

  if (isObject(obj2)) {
    Object.entries(obj2).forEach(([key, value]) => {
      if (isObject(value) && isObject(newObject[key])) {
        newObject[key] = deepFuse(newObject[key], value)
      } else {
        newObject[key] = value
      }
    })
  }

  return newObject
}

export const getRandomInt = (max = 100000000) => {
  return Math.floor(Math.random() * max)
}

export const inEnum = (enumeration, search) => {
  const action = (search) => Object.values(enumeration).includes(search)

  return search !== undefined ? action(search) : action
}

/**
 * @param {KeyboardEvent} first
 * @param {KeyboardEvent} second
 * @returns {boolean}
 */
export const areKeyboardEventsDifferent = (first, second) => (
  first !== null && second !== null && (
    first.type !== second.type ||
    first.key !== second.key ||
    first.ctrlKey !== second.ctrlKey ||
    first.metaKey !== second.metaKey ||
    first.shiftKey !== second.shiftKey ||
    first.altKey !== second.altKey
  )
)

/** Checks if `CTRL` or `⌘` (macOS) key are pressed */
export const isCtrlKeyPressed = e => e.ctrlKey || e.metaKey

export const getNextOrNull = (prev, keyboardKey, limits) => {
  const getters = {
    [Keyboard.ARROW_LEFT]: prev => ({ ...prev, x: prev.x[0] > 0 ? [prev.x[0] - 1, prev.x[1] - 1] : prev.x }),
    [Keyboard.ARROW_RIGHT]: prev => ({ ...prev, x: prev.x[0] < limits.x - 1 ? [prev.x[0] + 1, prev.x[1] + 1] : prev.x }),
    [Keyboard.ARROW_UP]: prev => ({ ...prev, y: prev.y[0] > 0 ? [prev.y[0] - 1, prev.y[1] - 1] : prev.y }),
    [Keyboard.ARROW_DOWN]: prev => ({ ...prev, y: prev.y[0] < limits.y - 1 ? [prev.y[0] + 1, prev.y[1] + 1] : prev.y })
  }
  let value = null

  if (prev !== null && isFunction(getters[keyboardKey])) {
    value = getters[keyboardKey](prev)
  }

  return value
}

export const getClassName = (value, data) => {
  let className = ''

  if (isFunction(value)) {
    className = value(data)
  } else if (isString(value)) {
    className = value
  }

  return className
}

export const handleStateSelectId = (handler, key) => id => {
  handler(data => ({ ...data, [key]: { id } }))
}

export const getErrors = errors => {
  return errors.missingFields.reduce((acc, f) => {
    acc[f] = true

    return acc
  }, {})
}

export const getFormError = (actif) => actif ? 'error-ant-border' : ''

export const isEqual = (value1, value2, key = null) => {
  let areEqual = false

  if (isArray(value1) && isArray(value2)) {
    key = key === null ? Properties.ID : key

    areEqual = value1.length === value2.length && value1.every((item1, index) => isEqual(item1[key], value2[index][key], key))
  } else if (typeof value1 === typeof value2) {
    areEqual = value1 === value2
  }

  return areEqual
}

export const subFlatten = (array) => {
  return array.reduce((acc, item) => {
    const itemArray = item.split(',')

    itemArray.forEach(subItem => {
      acc.push(subItem)
    })

    return acc
  }, [])
}

export {
  canonizeString,
  decodeFrenchCharacters,
  doesWeeksOverlap,
  downloadCSV,
  getJobResultWithPromise,
  downloadFileWithPromise,
  downloadFile,
  fetchJSON,
  filterSectionsAndSetRootSectionOfSchool,
  formatPositiveIntegerIntoHours,
  formatTimeFromMinutes,
  generalErrorHandler,
  getContextFromRoles,
  getCurrentUrl,
  getEnvironment,
  getGoogleTrackingId,
  getUrlParameterByName,
  hoursToMinutes,
  isNumeric,
  isPositive,
  isNumber,
  normalizedHoursString,
  objectDeepCopy,
  removeParentsOfSet,
  request,
  requestImage,
  requestMultipart,
  requestWithPromise,
  reverseDate,
  validateFormInput,
  urlSearchParamsToObject,
  objectToArray,
  arrayToObject,
  getItemValueRecursion,
  checkIfJsonIsCorrect,
  toFirstUpperCase,
  isObject,
  dateStringOrNull
}
